/**
 * Chart specification produced by the `visualiseData` AI-chat tool and rendered by the client
 * `ChatChart` component. The spec is *declarative and data-complete*: the model chooses the shape
 * (entity, grouping, chart type, title) via the tool input, and the server attaches the real
 * aggregated rows here — the model never authors the numbers. See
 * docs/plan/ai-chat-charts/plan.md.
 *
 * Browser-safe (no server imports), so both the server tool and the client renderer import it from
 * `@shared/schemas`.
 */
import { z } from 'zod';

/** The chart shapes the client `ChatChart` renderer supports. */
export const CHART_TYPES = ['line', 'column', 'bar', 'area', 'pie'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

/**
 * One plotted point. `*Key` fields carry stable identity (an FK id when the aggregate supplied one,
 * else the label), so two same-named entities — e.g. two owners called "John Smith" — never merge
 * into one bar/slice. `*Label` fields are the display text (formatted time buckets, sentence-case
 * enums). `y` is the count.
 */
export const chartDatumSchema = z.object({
    xKey: z.string(),
    xLabel: z.string(),
    /** Second dimension (time × category) — the colour/series split. Absent for single-dimension charts. */
    seriesKey: z.string().nullable().optional(),
    seriesLabel: z.string().nullable().optional(),
    y: z.number(),
});
export type ChartDatum = z.infer<typeof chartDatumSchema>;

export const chartSpecSchema = z.object({
    chartType: z.enum(CHART_TYPES),
    /** Sentence case, British English. e.g. "Complaints by month". */
    title: z.string(),
    xTitle: z.string().optional(),
    yTitle: z.string().optional(),
    /** Stack a 2-dimension (time × category) chart rather than group/dodge. */
    stacked: z.boolean().default(false),
    data: z.array(chartDatumSchema),
    /** Count of ALL records matching the request — so "N of total" context survives truncation. */
    total: z.number(),
    /**
     * Whether the client should render `total` as an "n = …" population caption. False for compare
     * mode, where `total` is a sum of independent (possibly overlapping) series counts — showing it
     * as a single number would misread as a record count.
     */
    showTotal: z.boolean().default(true),
    /** True when the aggregate dropped smaller groups (surfaced as an "Other" datum or a note). */
    truncated: z.boolean().default(false),
    /** Human caveat: truncation, chart-type coercion, or a "counts can overlap" denominator note. */
    notes: z.string().optional(),
});
export type ChartSpec = z.infer<typeof chartSpecSchema>;

/**
 * The full `visualiseData` tool result. `spec` is absent when there is nothing to plot (no matching
 * records); `formattedMessage` is a short summary the model can narrate alongside the rendered chart.
 */
export const chartToolResultSchema = z.object({
    success: z.boolean(),
    spec: chartSpecSchema.optional(),
    formattedMessage: z.string(),
    error: z.string().optional(),
});
export type ChartToolResult = z.infer<typeof chartToolResultSchema>;
